import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './SocialAuthCallback.css';

const SocialAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state');

      if (error) {
        setError(`Authentication failed: ${error}`);
        setStatus('error');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setStatus('error');
        return;
      }

      // Determine the provider from the state or URL
      const provider = state?.includes('google') ? 'google' : 'facebook';
      
      // Exchange the authorization code for tokens via backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/social/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          authorizationCode: code,
          redirectUri: window.location.origin + '/auth/callback'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Social authentication failed');
      }

      const data = await response.json();

      if (data.success) {
        // Login the user with the received data
        login(data.user, data.token);
        setStatus('success');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(data.message || 'Authentication failed');
      }

    } catch (error) {
      console.error('Social auth callback error:', error);
      setError(error.message || 'Authentication failed');
      setStatus('error');
    }
  };

  const handleRetry = () => {
    navigate('/auth');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (status === 'processing') {
    return (
      <div className="social-callback">
        <div className="callback-content">
          <div className="loading-spinner"></div>
          <h2>Completing Login...</h2>
          <p>Please wait while we complete your authentication.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="social-callback">
        <div className="callback-content error">
          <div className="error-icon">❌</div>
          <h2>Login Failed</h2>
          <p className="error-message">{error}</p>
          <div className="callback-actions">
            <button onClick={handleRetry} className="retry-button">
              Try Again
            </button>
            <button onClick={handleGoHome} className="home-button">
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="social-callback">
        <div className="callback-content success">
          <div className="success-icon">✅</div>
          <h2>Login Successful!</h2>
          <p>Redirecting you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default SocialAuthCallback; 